import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function AustralianRedCrossPlasmaPage() {
  useSEO({
    title: 'Australian Red Cross Plasma Donation | Design For',
    description: 'Increasing plasma donation through strategic engagement. Research-driven strategy for blood service donor recruitment and retention.',
    keywords: 'plasma donation, blood services, donor engagement, healthcare strategy, social impact',
    canonical: '/work/australian-red-cross-plasma',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Increasing plasma donation through strategic engagement',
    description: 'We partnered with Australian Red Cross to develop strategic frameworks for plasma donor recruitment and retention, improving donation rates and donor experience.',
    url: 'https://example.com/work/australian-red-cross-plasma',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Blood service organization - Plasma donation strategy',
    keywords: 'plasma donation, blood services, donor engagement, healthcare strategy, social impact',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10">
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Getting life saving plasma to those who need it most
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Australian Red Cross Blood Service</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                Plasma is critical to life saving treatments, from immune deficiencies to major surgeries, yet plasma donation is often overlooked by both the public and existing donors. The Blood Service needed to increase awareness, encourage more frequent donation, and improve the end to end experience inside donation centres.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                We took a hands on service design approach, working directly in centres to understand donor behaviour, emotional barriers, and operational constraints. By prototyping the service in real environments, we were able to test and refine ideas quickly and translate insight into practical change.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Conducted user research and co-design with donors and frontline staff',
                  'Designed current and future state experience maps for plasma donation',
                  'Prototyped a physical end to end donation service inside a live centre',
                  'Defined 4 priority initiatives to improve experience and retention',
                  'Achieved a 12% increase in plasma donations in the first 12 months',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="bg-gray-50 rounded-2xl overflow-hidden min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/6ebaadf85ce4b26263f432cd39d36f8d.jpeg"
                  alt="Plasma donor at Australian Red Cross Blood Service donation centre"
                  className="w-full h-full object-cover object-top"
                  style={{ minHeight: '340px' }}
                />
              </div>
              <div className="bg-gray-50 rounded-2xl overflow-hidden min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/08c732844e479b43a75692e7a85925b3.jpeg"
                  alt="Service design workshop for Australian Red Cross Blood Service plasma donation"
                  className="w-full h-full object-cover object-top"
                  style={{ minHeight: '340px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="australian-red-cross-plasma" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
